import { useCallback, useMemo, useReducer } from 'react';
import invariant from 'tiny-invariant';

import {
	Booking,
	BookingStateEnum,
	DataChange,
	ICheckInPassengerOptions,
} from '@mopla/data-models';
import { isBookingStatusCancelled } from '@mopla/utils';

import { mergeBookingsWithChanges } from '../utils/mergeBookingsWithChanges';

interface ICheckInFlowStore {
	checkInLegId: string | null;
	isCheckInActive: boolean;
	currentCheckInBookings: Booking[];
	dataChanges: DataChange[];
}

type TCheckInFlowStoreAction =
	| { type: 'SET_CHECK_IN_LEG_ID'; payload: string | null }
	| { type: 'START_CHECK_IN' }
	| { type: 'STOP_CHECK_IN' }
	| { type: 'SET_CURRENT_CHECK_IN_BOOKINGS'; payload: Booking[] }
	| { type: 'CLEAN_UP_DATA_CHANGES' }
	| { type: 'UPDATE_BOOKING'; payload: { booking: Booking } }
	| { type: 'PUSH_DATA_CHANGE'; payload: { dataChange: DataChange } };

const initialState: ICheckInFlowStore = {
	checkInLegId: null,
	isCheckInActive: false,
	currentCheckInBookings: [],
	dataChanges: [],
};

function reducer(
	state: ICheckInFlowStore,
	action: TCheckInFlowStoreAction
): ICheckInFlowStore {
	switch (action.type) {
		case 'SET_CHECK_IN_LEG_ID':
			return {
				...state,
				checkInLegId: action.payload,
			};

		case 'START_CHECK_IN':
			return {
				...state,
				isCheckInActive: true,
			};

		case 'STOP_CHECK_IN':
			return {
				...state,
				isCheckInActive: false,
			};

		case 'SET_CURRENT_CHECK_IN_BOOKINGS':
			return {
				...state,
				currentCheckInBookings: action.payload,
			};

		case 'PUSH_DATA_CHANGE': {
			const dataChangeIndex = state.dataChanges.findIndex(
				(dc) => dc.bookingId === action.payload.dataChange.bookingId
			);
			if (dataChangeIndex !== -1) {
				state.dataChanges.splice(dataChangeIndex, 1);
			}
			return {
				...state,
				dataChanges: [...state.dataChanges, action.payload.dataChange],
			};
		}
		case 'CLEAN_UP_DATA_CHANGES':
			return {
				...state,
				dataChanges: [],
			};

		case 'UPDATE_BOOKING': {
			const newBookings = [...state.currentCheckInBookings];

			const index = state.currentCheckInBookings.findIndex(
				(b) => b.bookingId === action.payload.booking.bookingId
			);

			invariant(
				index !== -1,
				'Something went wrong, booking not found in the list'
			);

			newBookings.splice(index, 1, action.payload.booking);

			return {
				...state,
				currentCheckInBookings: newBookings,
			};
		}

		default:
			return state;
	}
}

export const useCheckInFlowStore = () => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const updateBooking = useCallback(
		(
			booking: Booking,
			newState: BookingStateEnum,
			options?: ICheckInPassengerOptions
		) => {
			invariant(state.checkInLegId, 'Something went wrong, leg id is not set');

			const dataChange: DataChange = {
				changeId: Math.random().toString(),
				bookingId: booking.bookingId,
				insertTime: Date.now(),
				isSynced: false,
				ref: '', //not needed
				checkedInLegs: [state.checkInLegId],
				scheduledLegId: state.checkInLegId,
				type: newState,
				options,
			};

			const [mergedBooking] = mergeBookingsWithChanges([booking], [dataChange]);

			dispatch({
				type: 'UPDATE_BOOKING',
				payload: { booking: mergedBooking },
			});

			dispatch({
				type: 'PUSH_DATA_CHANGE',
				payload: { dataChange },
			});

			return { dataChange };
		},
		[state.checkInLegId]
	);

	const getCurrentCheckinBookings = useCallback(() => {
		const cancelledBookings: Booking[] = [];
		const checkedBookings: Booking[] = [];
		const notCheckedBookings: Booking[] = [];

		if (!state.checkInLegId) {
			return {
				cancelledBookings,
				checkedBookings,
				notCheckedBookings,
				allBookingsCheckedIn: false,
				bookingsCount: 0,
			};
		}

		state.currentCheckInBookings.forEach((b) => {
			const bookingLegStatus = b.checkedInScheduledLegs.find(
				(s) => s.scheduledLegId === state.checkInLegId
			);

			if (isBookingStatusCancelled(b.bookingState)) {
				cancelledBookings.push(b);
			} else if (bookingLegStatus?.isCheckedIn) {
				checkedBookings.push(b);
			} else {
				notCheckedBookings.push(b);
			}
		});

		/** There're 2 cases here:
		 *  * all currentCheckInBookings are checked
		 *  * there're no currentCheckInBookings (the leg has only exitingBookings)
		 *  */
		const allBookingsCheckedIn = notCheckedBookings.length === 0;

		return {
			cancelledBookings,
			checkedBookings,
			notCheckedBookings,
			allBookingsCheckedIn,
			bookingsCount: state.currentCheckInBookings.length,
		};
	}, [state.checkInLegId, state.currentCheckInBookings]);

	const currentCheckInBookingsGrouped = useMemo(
		() => getCurrentCheckinBookings(),
		[getCurrentCheckinBookings]
	);

	return {
		checkInLegId: state.checkInLegId,
		isCheckInActive: state.isCheckInActive,
		currentCheckInBookings: state.currentCheckInBookings,
		dataChanges: state.dataChanges,
		updateBooking,
		getCurrentCheckinBookings,
		currentCheckInBookingsGrouped,
		setCheckInLegId: useCallback((id: string | null) => {
			dispatch({ type: 'SET_CHECK_IN_LEG_ID', payload: id });
		}, []),
		setCurrentCheckInBookings: useCallback((bookings: Booking[]) => {
			dispatch({ type: 'SET_CURRENT_CHECK_IN_BOOKINGS', payload: bookings });
		}, []),
		startCheckIn: useCallback(() => {
			dispatch({ type: 'START_CHECK_IN' });
		}, []),
		stopCheckIn: useCallback(() => {
			dispatch({ type: 'STOP_CHECK_IN' });
		}, []),
		cleanUpDataChanges: useCallback(() => {
			dispatch({ type: 'CLEAN_UP_DATA_CHANGES' });
		}, []),
	};
};
